import {
  pageEvent,
  trackEvent,
  getCommonEventProperties,
  waitForAnalyticsLoad,
} from "@acko-tech/building-blocks.ui.acko-analytics";
import {
  getCurrentPageType,
  getExternalReferrer,
  segmentEventCommonProp,
} from "./utils";
import { getCookie, setCookie } from "cookies-next";

export enum EventType {
  PAGE_EVENT = "page",
  TRACK_EVENT = "track",
  IDENTIFY_EVENT = "identify",
}
export interface EventTrackerOptions {
  pageType?: string;
  additionalProps?: Record<string, any>;
  customWriteKey?: string;
}

export const trackEventWithCommonProps = async (
  eventName: string,
  eventType: EventType = EventType.TRACK_EVENT,
  options: EventTrackerOptions = {}
): Promise<void> => {
  try {
    const { pageType, additionalProps = {}, customWriteKey } = options;

    await waitForAnalyticsLoad();

    const commonProps = segmentEventCommonProp();
    const ackoCommonProps = getCommonEventProperties(
      pageType || getCurrentPageType()
    );

    let source = "";
    if (typeof window !== "undefined") {
      const storedSource = sessionStorage.getItem("referrerSource");

      if (storedSource) {
        source = storedSource;
      } else {
        const currentSource = getExternalReferrer();
        if (
          currentSource &&
          !currentSource.includes("ackodrive.com") &&
          !currentSource.includes("acko.com")
        ) {
          source = currentSource;
          sessionStorage.setItem("referrerSource", currentSource);
        }
      }
    }

    const eventProperties = {
      ...Object.fromEntries(
        Object.entries(ackoCommonProps).filter(([_, v]) => v != "")
      ),
      ...Object.fromEntries(
        Object.entries(commonProps).filter(([_, v]) => v !== "")
      ),
      ...additionalProps,
    };

    // Manually override source - always set it
    (eventProperties as any)["source"] = source || "";

    // Add user_id from ackodrive_token
    if (typeof window !== "undefined") {
      let userId = getCookie("user_id");
      console.log("Step 1 - Existing user_id from cookie:", userId);
      
      // Decode user_id token to check if expired
      let isExpired = false;
      if (userId) {
        try {
          const tokenParts = String(userId).split('.');
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));
            const exp = payload.exp;

            console.log("exp", exp);
            
            const currentTime = Math.floor(Date.now() / 1000);
            isExpired = exp && exp < currentTime;
            console.log("Step 2 - Token expiry check:", { exp, currentTime, isExpired });
          }
        } catch (error) {
          console.error("Error decoding user_id token:", error);
          isExpired = true;
        }
      }
      
      // If user_id is expired or not present, get from ackodrive_tokens
      if (!userId || isExpired) {
        console.log("Step 3 - Getting new token from ackodrive_tokens");
        const ackoDriveTokens = getCookie("ackodrive_tokens");
        
        if (ackoDriveTokens) {
          try {
            const tokensObj = JSON.parse(String(ackoDriveTokens));
            const accessToken = tokensObj.access;
            console.log("Step 4 - Access token extracted:", accessToken);
            
            if (accessToken) {
              userId = accessToken;
              setCookie("user_id", accessToken, {
                path: "/",
                maxAge: 14 * 24 * 60 * 60, // 14 days
              });
              console.log("Step 5 - user_id cookie set successfully");
            }
          } catch (error) {
            console.error("Error parsing ackodrive_tokens:", error);
          }
        }
      }
      
      // Set user_id in event properties
      console.log("Final - user_id for event:", userId || null);
      (eventProperties as any)["user_id"] = userId || null;
    }

    switch (eventType) {
      case EventType.PAGE_EVENT:
        await pageEvent(eventName, eventProperties, customWriteKey);
        break;
      case EventType.TRACK_EVENT:
        await trackEvent(eventName, eventProperties, customWriteKey);
        break;
      case EventType.IDENTIFY_EVENT:
        await trackEvent(eventName, eventProperties, customWriteKey);
        break;
      default:
        console.error(`Invalid event type: ${eventType}`);
        break;
    }
  } catch (error) {
    console.error("Error tracking event:", error);
  }
};
